import { theme } from 'antd';

// Light mode: verde elegante como primary
const PRIMARY_LIGHT_MODE = '#1a5c38';
const PRIMARY_LIGHT_BG = '#f0faf4';

// Dark mode: verde vibrante para que resalte sobre el fondo oscuro
const PRIMARY_DARK_MODE = '#00c96b';

const baseTokens = {
  fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  // Jerarquía de border radius moderna (2026)
  borderRadius: 10, // inputs, tags
  borderRadiusLG: 16, // cards
  borderRadiusSM: 8, // badges
  borderRadiusXS: 6,
  colorSuccess: '#22c55e',
  colorWarning: '#f59e0b',
  colorError: '#ef4444',
  fontSize: 15,
  fontSizeLG: 17,
  fontSizeSM: 13,
  lineHeight: 1.55,
  controlHeight: 44, // 44px mínimo táctil mobile
  controlHeightLG: 52,
  controlHeightSM: 36,
  fontWeightStrong: 600,
};

export const lightTheme = {
  algorithm: theme.defaultAlgorithm,
  token: {
    ...baseTokens,
    colorPrimary: PRIMARY_LIGHT_MODE,
    // antd links don't inherit colorPrimary; without this they stay default blue
    colorLink: PRIMARY_LIGHT_MODE,
    colorBgBase: '#ffffff',
    colorBgContainer: '#ffffff',
    colorBgLayout: '#f5f7f5',
    colorBgElevated: '#ffffff',
    colorBorder: '#e2e8e2',
    colorBorderSecondary: '#eef2ee',
    colorText: '#111811',
    colorTextSecondary: '#3d5c47',
    colorTextTertiary: '#7a9485',
    colorTextQuaternary: '#adc4b5',
    // Sombras suaves — elevación por capas, no por intensidad
    boxShadow: '0 1px 2px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.06)',
    boxShadowSecondary: '0 2px 4px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.08)',
  },
  components: {
    Layout: {
      bodyBg: '#f5f7f5',
      footerBg: '#ffffff',
      headerBg: '#ffffff',
    },
    Menu: {
      itemBg: 'transparent',
      itemSelectedBg: PRIMARY_LIGHT_BG,
      itemSelectedColor: PRIMARY_LIGHT_MODE,
      itemHoverColor: PRIMARY_LIGHT_MODE,
      itemHoverBg: PRIMARY_LIGHT_BG,
      itemColor: '#3d5c47',
      itemPaddingInline: 12,
    },
    Button: {
      primaryColor: '#ffffff',
      borderRadius: 10,
      controlHeight: 44,
      fontWeight: 600,
      // CTAs principales con pill radius
      defaultBorderColor: '#d0ddd4',
    },
    Card: {
      borderRadiusLG: 20,
      boxShadowTertiary: '0 1px 2px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.06)',
      paddingLG: 20,
    },
    Input: {
      borderRadius: 10,
      controlHeight: 44,
      colorBgContainer: '#ffffff',
      paddingInline: 14,
    },
    Select: {
      borderRadius: 10,
      controlHeight: 44,
    },
    Collapse: {
      borderRadius: 16,
      headerPadding: '14px 16px',
    },
    Modal: {
      borderRadiusLG: 24,
    },
    Divider: {
      colorSplit: '#eef2ee',
    },
    List: {
      itemPadding: '10px 0',
    },
    Avatar: {
      borderRadius: 50,
    },
  },
};

export const darkTheme = {
  algorithm: theme.darkAlgorithm,
  token: {
    ...baseTokens,
    colorPrimary: PRIMARY_DARK_MODE,
    // antd links don't inherit colorPrimary; without this they stay default blue
    colorLink: PRIMARY_DARK_MODE,
    // Superficies: zinc-900 family — más cálido que negro puro (referencia: Linear, Vercel)
    colorBgBase: '#111111',
    colorBgContainer: '#1c1c1c',
    colorBgLayout: '#111111',
    colorBgElevated: '#242424',
    colorBorder: '#2a2a2a',
    colorBorderSecondary: '#222222',
    colorText: '#f0f0f0',
    colorTextSecondary: '#a0a0a0',
    colorTextTertiary: '#666666',
    colorTextQuaternary: '#444444',
    // Sin sombras — la elevación se logra por diferencia de color de superficie
    boxShadow: 'none',
    boxShadowSecondary: 'none',
  },
  components: {
    Layout: {
      bodyBg: '#111111',
      footerBg: '#1c1c1c',
      headerBg: '#1c1c1c',
    },
    Menu: {
      itemBg: 'transparent',
      itemSelectedBg: '#2a2a2a',
      itemSelectedColor: PRIMARY_DARK_MODE,
      itemHoverColor: PRIMARY_DARK_MODE,
      itemHoverBg: '#222222',
      itemColor: '#a0a0a0',
      darkItemBg: 'transparent',
      darkItemSelectedBg: '#2a2a2a',
      itemPaddingInline: 12,
    },
    Button: {
      primaryColor: '#111111', // texto oscuro sobre acento claro
      borderRadius: 10,
      controlHeight: 44,
      fontWeight: 600,
      defaultBorderColor: '#2a2a2a',
      defaultColor: '#f0f0f0',
      defaultBg: '#1c1c1c',
    },
    Card: {
      borderRadiusLG: 20,
      colorBgContainer: '#1c1c1c',
      colorBorderSecondary: '#2a2a2a',
      paddingLG: 20,
    },
    Input: {
      borderRadius: 10,
      controlHeight: 44,
      colorBgContainer: '#1c1c1c',
      colorBorder: '#2a2a2a',
      paddingInline: 14,
      colorTextPlaceholder: '#555555',
    },
    Select: {
      borderRadius: 10,
      controlHeight: 44,
      colorBgContainer: '#1c1c1c',
      colorBorder: '#2a2a2a',
    },
    Collapse: {
      borderRadius: 16,
      colorBgContainer: '#1c1c1c',
      colorBorder: '#2a2a2a',
      headerPadding: '14px 16px',
    },
    Modal: {
      borderRadiusLG: 24,
      contentBg: '#1c1c1c',
      headerBg: '#1c1c1c',
    },
    Divider: {
      colorSplit: '#2a2a2a',
    },
    List: {
      colorSplit: '#2a2a2a',
      itemPadding: '10px 0',
    },
    Skeleton: {
      colorFillContent: '#2a2a2a',
      colorFill: '#222222',
    },
    Avatar: {
      borderRadius: 50,
      colorBorderBg: '#2a2a2a',
    },
  },
};
